// Validation utilities for forms

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

// Phone number validation (Indian format)
export const validatePhone = (phone: string): ValidationResult => {
  const cleanPhone = phone.replace(/\D/g, '');
  
  if (!cleanPhone) {
    return { isValid: false, error: 'Phone number is required' };
  }
  
  if (cleanPhone.length !== 10) {
    return { isValid: false, error: 'Phone number must be 10 digits' };
  }
  
  if (!/^[6-9]/.test(cleanPhone)) {
    return { isValid: false, error: 'Phone number must start with 6, 7, 8, or 9' };
  }
  
  return { isValid: true };
};

// Email validation
export const validateEmail = (email: string): ValidationResult => {
  if (!email) {
    return { isValid: false, error: 'Email is required' };
  }
  
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  
  if (email.length > 254) {
    return { isValid: false, error: 'Email address is too long' };
  }
  
  return { isValid: true };
};

// Name validation
export const validateName = (name: string, fieldName: string = 'Name'): ValidationResult => {
  if (!name) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  
  const trimmedName = name.trim();
  
  if (trimmedName.length < 2) {
    return { isValid: false, error: `${fieldName} must be at least 2 characters` };
  }
  
  if (trimmedName.length > 50) {
    return { isValid: false, error: `${fieldName} must be less than 50 characters` };
  }
  
  if (!/^[a-zA-Z\s'-]+$/.test(trimmedName)) {
    return { isValid: false, error: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes` };
  }
  
  return { isValid: true };
};

// Address validation
export const validateAddress = (address: string): ValidationResult => {
  if (!address) {
    return { isValid: false, error: 'Address is required' };
  }
  
  const trimmedAddress = address.trim();
  
  if (trimmedAddress.length < 10) {
    return { isValid: false, error: 'Address must be at least 10 characters' };
  }
  
  if (trimmedAddress.length > 200) {
    return { isValid: false, error: 'Address must be less than 200 characters' };
  }
  
  // Check for potentially malicious content
  const suspiciousPatterns = /<script|javascript:|data:|vbscript:|onload=|onerror=/i;
  if (suspiciousPatterns.test(trimmedAddress)) {
    return { isValid: false, error: 'Address contains invalid characters' };
  }
  
  return { isValid: true };
};

// City validation
export const validateCity = (city: string): ValidationResult => {
  if (!city) {
    return { isValid: false, error: 'City is required' };
  }
  
  const trimmedCity = city.trim();
  
  if (trimmedCity.length < 2) {
    return { isValid: false, error: 'City must be at least 2 characters' };
  }
  
  if (trimmedCity.length > 50) {
    return { isValid: false, error: 'City must be less than 50 characters' };
  }
  
  if (!/^[a-zA-Z\s'-]+$/.test(trimmedCity)) {
    return { isValid: false, error: 'City can only contain letters, spaces, hyphens, and apostrophes' };
  }
  
  return { isValid: true };
};

// State validation
export const validateState = (state: string): ValidationResult => {
  if (!state) {
    return { isValid: false, error: 'State is required' };
  }
  
  const trimmedState = state.trim();
  
  if (trimmedState.length < 2) {
    return { isValid: false, error: 'State must be at least 2 characters' };
  }
  
  if (trimmedState.length > 50) {
    return { isValid: false, error: 'State must be less than 50 characters' };
  }
  
  if (!/^[a-zA-Z\s'-]+$/.test(trimmedState)) {
    return { isValid: false, error: 'State can only contain letters, spaces, hyphens, and apostrophes' };
  }
  
  return { isValid: true };
};

// ZIP code validation (Indian PIN code)
export const validateZipCode = (zipCode: string): ValidationResult => {
  if (!zipCode) {
    return { isValid: false, error: 'ZIP code is required' };
  }
  
  const cleanZipCode = zipCode.replace(/\D/g, '');
  
  if (cleanZipCode.length !== 6) {
    return { isValid: false, error: 'ZIP code must be 6 digits' };
  }
  
  if (!/^[1-9]/.test(cleanZipCode)) {
    return { isValid: false, error: 'ZIP code cannot start with 0' };
  }
  
  return { isValid: true };
};

// OTP validation
export const validateOTP = (otp: string): ValidationResult => {
  if (!otp) {
    return { isValid: false, error: 'OTP is required' };
  }
  
  const cleanOTP = otp.replace(/\D/g, '');
  
  if (cleanOTP.length !== 6) {
    return { isValid: false, error: 'OTP must be 6 digits' };
  }
  
  return { isValid: true };
};

// Card number validation (basic Luhn algorithm)
export const validateCardNumber = (cardNumber: string): ValidationResult => {
  if (!cardNumber) {
    return { isValid: false, error: 'Card number is required' };
  }
  
  const cleanCardNumber = cardNumber.replace(/\D/g, '');
  
  if (cleanCardNumber.length < 13 || cleanCardNumber.length > 19) {
    return { isValid: false, error: 'Card number must be between 13-19 digits' };
  }
  
  // Luhn algorithm
  let sum = 0;
  let isEven = false;
  
  for (let i = cleanCardNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(cleanCardNumber[i]);
    
    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    
    sum += digit;
    isEven = !isEven;
  }
  
  if (sum % 10 !== 0) {
    return { isValid: false, error: 'Invalid card number' };
  }
  
  return { isValid: true };
};

// Expiry date validation (MM/YY format)
export const validateExpiryDate = (expiryDate: string): ValidationResult => {
  if (!expiryDate) {
    return { isValid: false, error: 'Expiry date is required' };
  }
  
  const expiryRegex = /^(0[1-9]|1[0-2])\/([0-9]{2})$/;
  
  if (!expiryRegex.test(expiryDate)) {
    return { isValid: false, error: 'Expiry date must be in MM/YY format' };
  }
  
  const [month, year] = expiryDate.split('/');
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear() % 100;
  const currentMonth = currentDate.getMonth() + 1;
  
  const cardYear = parseInt(year);
  const cardMonth = parseInt(month);
  
  if (cardYear < currentYear || (cardYear === currentYear && cardMonth < currentMonth)) {
    return { isValid: false, error: 'Card has expired' };
  }
  
  if (cardYear > currentYear + 20) {
    return { isValid: false, error: 'Invalid expiry date' };
  }
  
  return { isValid: true };
};

// CVV validation
export const validateCVV = (cvv: string): ValidationResult => {
  if (!cvv) {
    return { isValid: false, error: 'CVV is required' };
  }
  
  const cleanCVV = cvv.replace(/\D/g, '');
  
  if (cleanCVV.length < 3 || cleanCVV.length > 4) {
    return { isValid: false, error: 'CVV must be 3 or 4 digits' };
  }
  
  return { isValid: true };
};

// UPI ID validation
export const validateUPIId = (upiId: string): ValidationResult => {
  if (!upiId) {
    return { isValid: false, error: 'UPI ID is required' };
  }
  
  const upiRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/;
  
  if (!upiRegex.test(upiId)) {
    return { isValid: false, error: 'Please enter a valid UPI ID (e.g., name@paytm)' };
  }
  
  if (upiId.length > 50) {
    return { isValid: false, error: 'UPI ID is too long' };
  }
  
  return { isValid: true };
};

// Input sanitization
export const sanitizeInput = (input: string): string => {
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/data:/gi, '') // Remove data: protocol
    .replace(/vbscript:/gi, '') // Remove vbscript: protocol
    .substring(0, 1000); // Limit length
};

// Rate limiting check (client-side)
const requestCounts = new Map<string, { count: number; timestamp: number }>();

export const checkRateLimit = (identifier: string, maxRequests: number = 5, windowMs: number = 60000): boolean => {
  const now = Date.now();
  const record = requestCounts.get(identifier);
  
  if (!record || now - record.timestamp > windowMs) {
    requestCounts.set(identifier, { count: 1, timestamp: now });
    return true;
  }
  
  if (record.count >= maxRequests) {
    return false;
  }
  
  record.count++;
  return true;
};